import { Card, Col, Empty, Progress, Row, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  MobileOutlined,
  PoweroffOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDashboardStats } from '@/hooks/useDashboard';
import { useAuthStore } from '@/stores/authStore';
import { CommandStatusColors, CommandStatusLabels } from '@/types/command';

const { Title, Text } = Typography;

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data: stats, isLoading } = useDashboardStats();

  if (isLoading || !stats) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const activePercentage = stats.totalDevices > 0
    ? Math.round((stats.activeDevices / stats.totalDevices) * 100)
    : 0;

  const approvedAppsPercentage = stats.totalApps > 0
    ? Math.round((stats.approvedApps / stats.totalApps) * 100)
    : 0;

  return (
    <div>
      <Title level={3} style={{ margin: '0 0 8px 0' }}>
        <DashboardOutlined style={{ marginRight: 12 }} />
        Dashboard
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Bienvenido, {user?.firstName}. Auto-refresh cada 30 segundos.
      </Text>

      {/* KPIs principales */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Dispositivos totales"
              value={stats.totalDevices}
              prefix={<MobileOutlined style={{ color: 'var(--color-primary)' }} />}
            />
            <Progress
              percent={activePercentage}
              size="small"
              status="active"
              format={() => `${stats.activeDevices} activos`}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Activos"
              value={stats.activeDevices}
              prefix={<CheckCircleOutlined style={{ color: 'var(--color-success)' }} />}
              suffix={<Text type="secondary" style={{ fontSize: 14 }}>/ {stats.totalDevices}</Text>}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sin reportar"
              value={stats.offlineDevices}
              prefix={<WifiOutlined style={{ color: 'var(--color-warning)' }} />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Comandos pendientes"
              value={stats.pendingCommands}
              prefix={<ThunderboltOutlined style={{ color: 'var(--color-primary)' }} />}
              suffix={
                stats.commandsLast24h > 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {stats.commandsLast24h} en 24h
                  </Text>
                ) : null
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Stats secundarios */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Enterprises"
              value={stats.totalEnterprises}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Perfiles"
              value={stats.totalProfiles}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Aplicaciones"
              value={stats.totalApps}
              prefix={<AppstoreOutlined />}
              suffix={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stats.approvedApps} aprobadas ({approvedAppsPercentage}%)
                </Text>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Listas */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Devices por Enterprise */}
        <Col xs={24} md={12}>
          <Card title={<Space><TeamOutlined />Dispositivos por Enterprise</Space>}>
            {stats.devicesByEnterprise.length === 0 ? (
              <Empty />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {stats.devicesByEnterprise.map((e) => {
                  const pct = stats.totalDevices > 0 ? (e.count / stats.totalDevices) * 100 : 0;
                  return (
                    <div key={e.enterpriseId}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text>{e.enterpriseName}</Text>
                        <Text strong>{e.count}</Text>
                      </div>
                      <Progress percent={pct} size="small" showInfo={false} />
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>
        </Col>

        {/* Devices por estado */}
        <Col xs={24} md={12}>
          <Card title={<Space><MobileOutlined />Dispositivos por estado</Space>}>
            {stats.devicesByState.length === 0 ? (
              <Empty />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {stats.devicesByState.map((s) => {
                  const pct = stats.totalDevices > 0 ? (s.count / stats.totalDevices) * 100 : 0;
                  return (
                    <div key={s.state}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Tag>{s.state}</Tag>
                        <Text strong>{s.count}</Text>
                      </div>
                      <Progress percent={pct} size="small" showInfo={false} />
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      {/* Comandos recientes */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title={<Space><ClockCircleOutlined />Comandos recientes</Space>}>
            <Table
              size="small"
              rowKey="id"
              dataSource={stats.recentCommands}
              pagination={false}
              locale={{ emptyText: 'Sin comandos recientes' }}
              columns={[
                {
                  title: 'Tipo',
                  dataIndex: 'commandType',
                  render: (v) => <Tag>{v}</Tag>,
                },
                {
                  title: 'Dispositivo',
                  dataIndex: 'deviceLabel',
                },
                {
                  title: 'Estado',
                  dataIndex: 'status',
                  render: (v: string) => (
                    <Tag color={CommandStatusColors[v as keyof typeof CommandStatusColors] ?? 'default'}>
                      {CommandStatusLabels[v as keyof typeof CommandStatusLabels] ?? v}
                    </Tag>
                  ),
                },
                {
                  title: 'Fecha',
                  dataIndex: 'requestedAt',
                  render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm'),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
